// utils.ts (o utils.js)
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export class Utils {
    /**
     * Formatea una fecha a dd/mm/yyyy.
     * @param fecha La fecha a formatear.
     * @returns La fecha formateada.
     */
    static formatearFecha(fecha: Date | string): string {
      if (!fecha) {
        return '';
      }
  
      const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
      const dia = date.getDate().toString().padStart(2, '0');
      const mes = (date.getMonth() + 1).toString().padStart(2, '0');
      const anio = date.getFullYear();
      return `${dia}/${mes}/${anio}`;
    }
  
    /**
     * Convierte un texto a mayúsculas.
     * @param texto El texto a convertir.
     * @returns El texto en mayúsculas.
     */
    static convertirAMayusculas(texto: string): string {
      return texto.toUpperCase();
    }
  
    /**
     * Valida si un email tiene un formato correcto.
     * @param email El email a validar.
     * @returns Verdadero si el email es válido, falso de lo contrario.
     */
    static validarEmail(email: string): boolean {
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return regex.test(email);
    }
  
    /**
     * Trunca un texto a una longitud máxima.
     * @param texto El texto a truncar.
     * @param longitudMaxima La longitud máxima del texto.
     * @returns El texto truncado.
     */
    static truncarTexto(texto: string, longitudMaxima: number): string {
      if (texto.length <= longitudMaxima) {
        return texto;
      }
      return texto.substring(0, longitudMaxima) + '...';
    }
  
    // Agrega aquí más funciones de utilidad según tus necesidades
    static  verificarVigenciaDocumento(fechaVencimiento: Date | string): string {
      if (!fechaVencimiento) {
        return 'Fecha de vencimiento no proporcionada';
      }
    
      const fechaVencimientoDate = typeof fechaVencimiento === 'string' ? new Date(fechaVencimiento) : fechaVencimiento;
      const fechaActual = new Date();
    
      // Eliminar la parte de la hora para comparar solo las fechas
      fechaVencimientoDate.setHours(0, 0, 0, 0);
      fechaActual.setHours(0, 0, 0, 0);
    
      if (fechaVencimientoDate < fechaActual) {
        return 'Vencido';
      }
    
      // Definir cuántos días antes se considera "próximo a vencer" (puedes ajustar este valor)
      const diasProximoAVencer = 30;
    
      const fechaProximoVencimiento = new Date(fechaVencimientoDate);
      fechaProximoVencimiento.setDate(fechaProximoVencimiento.getDate() - diasProximoAVencer);
    
      if (fechaProximoVencimiento <= fechaActual) {
        return 'Próximo a vencer';
      }
    
      return 'Vigentes';
    }

    static exportToExcel(data: any[], fileName: string, sheetName: string = 'Hoja1') {
      const workbook: XLSX.WorkBook = XLSX.utils.book_new();
      let formattedData = data.map(item => ({
        ...item,
        fecha: item.fecha ? Utils.formatearFecha(item.fecha) : '' // Formateo de fecha
      }));
    
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
      const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob: Blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    
      saveAs(blob, `${fileName}.xlsx`);
    }
    
    static exportVisibleTableToExcel(tableId: string, fileName: string, sheetName: string = 'Hoja1') {
      const table: HTMLTableElement | null = document.getElementById(tableId) as HTMLTableElement;
    
      if (!table) {
        console.error('Tabla no encontrada');
        return;
      }
    
      const rows: any[] = [];
      
      // Recorre todas las filas de la tabla
      for (let i = 0; i < table.rows.length; i++) {
        const row = table.rows[i];
        const rowData: any[] = [];
        
        // Excluir la última celda de cada fila (columna "Acciones")
        for (let j = 0; j < row.cells.length - 1; j++) {  
          rowData.push(row.cells[j].innerText.trim());
        }
        
        rows.push(rowData);
      }
    
      // Crear un libro de Excel y agregar la hoja con los datos
      const workbook: XLSX.WorkBook = XLSX.utils.book_new();
      const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(rows);
      
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
      // Generar archivo Excel y descargar
      const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob: Blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    
      saveAs(blob, `${fileName}.xlsx`);
    }


    //exporta a
    static exportToPDF(idTabla: string, nombreArchivo: string, titulo: string, excludeLastColumn: boolean = false) {
      const doc = new jsPDF('l', 'mm', 'a4'); // Formato horizontal A4
      const fechaHora = new Date().toLocaleString(); // Fecha y hora actual
    
      // Agregar título centrado
      doc.setFontSize(14);
      doc.text(titulo, doc.internal.pageSize.width / 2, 15, { align: 'center' });
    
      // Obtener la tabla
      const tabla = document.getElementById(idTabla);
      if (!tabla) {
        console.error(`No se encontró la tabla con el ID: ${idTabla}`);
        return;
      }
    
      // Extraer datos visibles de la tabla
      const filas = Array.from(tabla.getElementsByTagName('tr')).slice(1); // Omitimos la cabecera
      const data = filas.map(row => {
        const celdas = Array.from(row.getElementsByTagName('td'));
        const cells = excludeLastColumn ? celdas.slice(0, -1) : celdas;
        return cells.map(cell => cell.innerText.trim());
      });
    
      // Obtener encabezado de la tabla
      const allHeaders = Array.from(tabla.getElementsByTagName('th'));
      const cabecera = (excludeLastColumn ? allHeaders.slice(0, -1) : allHeaders).map(th => th.innerText.trim());
    
      // Generar la tabla en el PDF
      autoTable(doc, {
        head: [cabecera],
        body: data,
        startY: 20,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [50, 48, 50], textColor: 255, fontStyle: 'bold' },
        didDrawPage: function (data) {
          const pageCount = doc.getNumberOfPages();
          const currentPage = doc.getCurrentPageInfo().pageNumber;
    
          // Posición del footer
          const pageHeight = doc.internal.pageSize.height;
          doc.setFontSize(10);
          doc.text(`Página ${currentPage} de ${pageCount}`, 10, pageHeight - 10);
          doc.text('Generado ADMIN SEAF', doc.internal.pageSize.width / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Generado: ${fechaHora}`, doc.internal.pageSize.width - 30, pageHeight - 10, { align: 'right' });
        }
      });
    
      // Guardar el PDF con el nombre especificado
      doc.save(`${nombreArchivo}.pdf`);
    }

    static exportDataToPDF(headers: string[], rows: Array<Array<string | number>>, nombreArchivo: string, titulo: string) {
      const doc = new jsPDF('l', 'mm', 'a4');
      const fechaHora = new Date().toLocaleString();

      doc.setFontSize(14);
      doc.text(titulo, doc.internal.pageSize.width / 2, 15, { align: 'center' });

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 20,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [50, 48, 50], textColor: 255, fontStyle: 'bold' },
        didDrawPage: () => {
          const pageCount = doc.getNumberOfPages();
          const currentPage = doc.getCurrentPageInfo().pageNumber;
          const pageHeight = doc.internal.pageSize.height;

          doc.setFontSize(10);
          doc.text(`Página ${currentPage} de ${pageCount}`, 10, pageHeight - 10);
          doc.text('Generado ADMIN SEAF', doc.internal.pageSize.width / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Generado: ${fechaHora}`, doc.internal.pageSize.width - 30, pageHeight - 10, { align: 'right' });
        }
      });

      doc.save(`${nombreArchivo}.pdf`);
    }

    static exportPagoRecibo(data: {
      placa: string;
      idPeriodo: string | number;
      fechaInicio?: string | null;
      fechaFin?: string | null;
      valorPeriodo?: number;
      totalPagadoPeriodo?: number;
      saldoPendientePeriodo?: number;
      idPago?: string | number;
      fechaPago?: string | null;
      tipoPago?: string;
      formaPago?: string;
      valorPago?: number;
      usuario?: string;
      observacion?: string;
    }): void {
      const doc = new jsPDF('p', 'mm', 'a4');
      const now = new Date();
      const fechaGeneracion = now.toLocaleString('es-CO');
      const adminUrl = 'https://indesoftsas.com';
      const drawFooter = (pageNumber: number, totalPages: number) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();
        const footerLinkText = 'Impreso desde ADMIN SEAF';
        const footerLinkWidth = doc.getTextWidth(footerLinkText);
        const footerLinkX = (pageWidth - footerLinkWidth) / 2;

        doc.setDrawColor(220, 220, 220);
        doc.line(15, pageHeight - 14, pageWidth - 15, pageHeight - 14);
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`Generado: ${fechaGeneracion}`, 15, pageHeight - 8);
        doc.setTextColor(0, 0, 0);
        doc.textWithLink(footerLinkText, footerLinkX, pageHeight - 8, { url: adminUrl });
        doc.text(`Pagina ${pageNumber} de ${totalPages}`, pageWidth - 15, pageHeight - 8, { align: 'right' });
      };

      const fechaInicio = data.fechaInicio ? Utils.formatearFecha(data.fechaInicio) : 'N/A';
      const fechaFin = data.fechaFin ? Utils.formatearFecha(data.fechaFin) : 'N/A';
      const fechaPago = data.fechaPago ? Utils.formatearFecha(data.fechaPago) : 'N/A';

      const fmtMoney = (value?: number) =>
        new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: 'COP',
          maximumFractionDigits: 0
        }).format(Number(value ?? 0));

      doc.setFontSize(16);
      doc.text('RECIBO DE PAGO DE RODAMIENTO', 105, 16, { align: 'center' });

      doc.setFontSize(10);
      doc.text(`Generado: ${fechaGeneracion}`, 15, 24);
      doc.text(`Placa: ${data.placa || 'N/A'}`, 15, 30);
      doc.text(`Periodo ID: ${String(data.idPeriodo ?? 'N/A')}`, 105, 30);

      const receiptTableConfig: any = {
        theme: 'grid',
        margin: { left: 14, right: 14, bottom: 20 },
        tableWidth: 'auto',
        headStyles: {
          fillColor: [50, 50, 50],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center'
        },
        styles: {
          fontSize: 10,
          cellPadding: 3,
          textColor: [31, 41, 55],
          lineColor: [226, 232, 240],
          lineWidth: 0.2
        },
        bodyStyles: {
          fillColor: [255, 255, 255]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 62, fontStyle: 'bold' },
          1: { cellWidth: 'auto' }
        }
      };

      autoTable(doc, {
        startY: 36,
        ...receiptTableConfig,
        head: [['Detalle del periodo', 'Valor']],
        body: [
          ['Rango del periodo', `${fechaInicio} - ${fechaFin}`],
          ['Valor del periodo', fmtMoney(data.valorPeriodo)],
          ['Total pagado del periodo', fmtMoney(data.totalPagadoPeriodo)],
          ['Saldo pendiente del periodo', fmtMoney(data.saldoPendientePeriodo)]
        ]
      });

      const yAfterPeriodo = (doc as any).lastAutoTable?.finalY ?? 70;

      autoTable(doc, {
        startY: yAfterPeriodo + 6,
        ...receiptTableConfig,
        head: [['Detalle del pago', 'Valor']],
        body: [
          ['Pago ID', String(data.idPago ?? 'N/A')],
          ['Fecha de pago', fechaPago],
          ['Tipo de pago', data.tipoPago || 'N/A'],
          ['Forma de pago', data.formaPago || 'N/A'],
          ['Valor pagado', fmtMoney(data.valorPago)],
          ['Registrado por', data.usuario || 'N/A'],
          ['Observacion', data.observacion || '-']
        ]
      });

      const totalPages = doc.getNumberOfPages();
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        doc.setPage(pageNumber);
        drawFooter(pageNumber, totalPages);
      }

      const fileName = `Recibo_Pago_${data.placa || 'Vehiculo'}_${String(data.idPeriodo ?? 'Periodo')}_${String(data.idPago ?? 'Pago')}`;
      doc.save(`${fileName}.pdf`);
    }
    


    
    
  }
  